import {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import '../../assets/styles/account/account.scss';
import usePageMeta from '../../services/page-meta';
import {buildFormBody, isValidEmailOrPhone, normalizePhoneOrEmail, parseJsonResponse} from './helpers';

export default function RestorePasswordPage() {
    const {t} = useTranslation();
    usePageMeta({
        title: t('meta.restorePassword.title', t('restorePassword.header')),
        description: t('meta.restorePassword.description', t('app.description')),
        keywords: t('meta.restorePassword.keywords', t('app.keywords')),
    });

    const [username, setUsername] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [backendError, setBackendError] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [actions, setActions] = useState({
        restoreUrl: '/api/v1/account/restore-password',
    });

    useEffect(() => {
        let isCancelled = false;

        fetch('/api/v1/account/restore-password', {credentials: 'same-origin'})
            .then(parseJsonResponse)
            .then(({response, data}) => {
                if (isCancelled || !response.ok) {
                    return;
                }

                const restoreUrl = data?.actions?.restoreUrl;
                if (restoreUrl) {
                    setActions({restoreUrl});
                }
            })
            .catch(() => {
                // Ignore bootstrap errors and keep default action URL.
            });

        return () => {
            isCancelled = true;
        };
    }, []);

    const handleSubmit = (event) => {
        event.preventDefault();
        if (isSubmitting) {
            return;
        }

        setUsernameError('');
        setBackendError('');

        const normalizedUsername = normalizePhoneOrEmail(username);
        if (!normalizedUsername) {
            setUsernameError(t('restorePassword.errors.requiredUsername'));
            return;
        }

        if (!isValidEmailOrPhone(normalizedUsername)) {
            setUsernameError(t('restorePassword.errors.invalidUsername'));
            return;
        }

        setIsSubmitting(true);

        fetch(actions.restoreUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
            body: buildFormBody({
                username: normalizedUsername,
            }),
        })
            .then(parseJsonResponse)
            .then(({response, data}) => {
                if (!response.ok) {
                    throw data;
                }

                const redirectUrl = data?.data?.url;
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                    return;
                }

                setIsSuccess(true);
            })
            .catch((errorPayload) => {
                setBackendError(errorPayload?.error?.message || t('restorePassword.errors.invalidUsername'));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <section className="section flex-center">
            <div className={`authentication-form restore${isSuccess ? ' js-restore-password-block f_pss2' : ' js-restore-password-block'}`}>
                <h1>{t('restorePassword.header')}</h1>
                {!isSuccess && (
                    <form className="form" action="#" method="post" id="restore-password" onSubmit={handleSubmit}>
                        <div className="form-group block-static-height field_block">
                            <label htmlFor="username" className={`form-label${username ? ' active' : ''}`}>
                                {t('restorePassword.usernameLabel')}
                            </label>
                            <input
                                type="text"
                                name="username"
                                id="username"
                                className={`form${usernameError || backendError ? ' error-field' : ''}`}
                                value={username}
                                onChange={(event) => {
                                    setUsername(event.target.value);
                                    setUsernameError('');
                                    setBackendError('');
                                }}
                            />
                            <span className={`error_text js-backend-error${usernameError || backendError ? '' : ' hidden'}`}>
                                {usernameError || backendError}
                            </span>
                        </div>
                        <div className="submit-block">
                            <input type="submit" className="btn" value={t('restorePassword.submit')} disabled={isSubmitting} />
                        </div>
                    </form>
                )}
                {isSuccess && (
                    <div className="success-block js-success">
                        <p>{t('restorePassword.successCaption')}</p>
                        <div className="submit-block">
                            <Link to="/" className="btn">{t('restorePassword.toHome')}</Link>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}
